import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Users, Plus, Pencil, UserCheck, Loader2, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { impersonationService } from '@/services/impersonationService';
import { useToast } from '@/hooks/useToast';
import { CONTACT_ROLE_OPTIONS } from '@/types/investor';
import type { InvestorContact } from '@/types/investor';

interface InvestorContactsTableProps {
  investorId: string;
  contacts: InvestorContact[];
}

export function InvestorContactsTable({
  investorId,
  contacts,
}: InvestorContactsTableProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [impersonatingContactId, setImpersonatingContactId] = useState<
    string | null
  >(null);

  const handleImpersonate = async (
    e: React.MouseEvent,
    contact: InvestorContact
  ) => {
    e.stopPropagation();
    if (!contact.id || !investorId) return;

    setImpersonatingContactId(contact.id);

    try {
      const response = await impersonationService.startImpersonation({
        contact_id: contact.id,
        investor_id: investorId,
      });
      impersonationService.openPortalWithImpersonation(response, '/portal');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to start impersonation';
      toast({
        title: 'Impersonation Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setImpersonatingContactId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Contacts</span>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              navigate(`/app/investors/${investorId}/contacts/new`)
            }
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Contact</span>
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No contacts yet. Add a contact to get started.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Primary</TableHead>
                <TableHead>Portal Access</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/app/investors/${investorId}/contacts/${contact.id}`
                    )
                  }
                >
                  <TableCell className="font-medium">
                    {contact.first_name} {contact.last_name}
                    {contact.title && (
                      <span className="text-muted-foreground ml-1">
                        ({contact.title})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {CONTACT_ROLE_OPTIONS[contact.role] || contact.role}
                  </TableCell>
                  <TableCell>{contact.email || '-'}</TableCell>
                  <TableCell>{contact.phone || '-'}</TableCell>
                  <TableCell>
                    {contact.is_primary && (
                      <Badge variant="secondary">
                        <Check className="h-3 w-3 mr-1" />
                        Primary
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {contact.portal_role_id ? (
                      <Badge variant="outline">
                        {contact.portal_role || 'Portal Access'}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {contact.portal_role_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleImpersonate(e, contact)}
                          disabled={impersonatingContactId === contact.id}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Impersonate in Investor Portal"
                        >
                          {impersonatingContactId === contact.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/app/investors/${investorId}/contacts/${contact.id}`
                          );
                        }}
                        title="Edit contact"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
